import { useEffect, useRef } from "react";
import { debounce } from "lodash";
import Auth from "../../../Auth";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";

const DEFAULT_COLLAPSED_SECTIONS = {
  seed: false,
  genre: false,
  style: false,
  synopsis: false,
  characters: false,
  worldbuilding: false,
  outline: false,
};

/**
 * Sets up persistence for collapsed Story Bible sections.
 * Loads collapsed state from project flags on mount, and saves changes back (debounced).
 * Use storyBibleStore directly for state and actions (collapsedSections, toggleSectionCollapsed).
 */
export default function useCollapsedSections({ projectId }) {
  const collapsedSections = useStoryBibleStore((state) => state.collapsedSections);
  const setCollapsedSections = useStoryBibleStore((state) => state.setCollapsedSections);

  const initialLoadDone = useRef(false);

  useEffect(() => {
    const loadCollapsedSections = async () => {
      try {
        const flags = await Auth.fetchWithIdToken(`/api/projects/${projectId}/flags`, {
          method: "POST",
        });

        if (flags?.collapsedSections) {
          setCollapsedSections({
            ...DEFAULT_COLLAPSED_SECTIONS,
            ...flags.collapsedSections,
          });
        }
        initialLoadDone.current = true;
      } catch (e) {
        console.error("Failed to load collapsed sections", e);
        initialLoadDone.current = true;
      }
    };

    loadCollapsedSections();
  }, [projectId, setCollapsedSections]);

  const saveCollapsedSections = useRef(
    debounce(async (projectId, sections) => {
      try {
        await Auth.fetchWithIdToken(`/api/projects/${projectId}/flags`, {
          method: "PUT",
          flags: { collapsedSections: sections },
        });
      } catch (e) {
        console.error("Failed to save collapsed sections", e);
      }
    }, 500)
  );

  useEffect(() => {
    if (!initialLoadDone.current) return;
    saveCollapsedSections.current(projectId, collapsedSections);
  }, [collapsedSections, projectId]);
}
